import * as React from 'react';

import { WBIcon, Pressable, WBText, View, ViewProps } from '@/core';
import { StyleSheet } from '@/core/StyleSheet';
import styles from './style';

export interface RadioProps {
	/**
	 * Determines whether the radio should show as checked.
	 */
	checked?: boolean;
	/**
	 * Determines disabled state for the radio. Disables any user interaction when true.
	 */
	isDisabled?: boolean;
	/**
	 * Label giving context to what is selected.
	 */
	label?: string;
	/**
	 * Callback called when the user activates the radio. The callback should toggle the given <checked> property to propagate the new state into the component.
	 */
	onChange?: (value: boolean) => void;
	/**
	 * An object to style the outside container of the component.
	 */
	style?: ViewProps['style'];
}

/**
 * WBRadio is an UI element that allow the user to select options.
 *
 * The radio component displays a radio button with the given <checked> state, accompanied by the given <label>. On click of
 *  either, an event is fired and it's up to the implementer to pass on the new checked state into the component.
 *
 * This component was chosen over the react-native-checkbox (boxtype circle) component to allow for customised UI and easier
 *  state management. Also, the boxtype circle shows up as a checkbox on Android.
 *
 * Example usecases:
 * - WBRadio to make a choice of office on the slots-enabled 'create reservation' screen.
 *
 * @example
 * <WBRadio label='WBRadio example' checked={true} />
 */

export const WBRadio: React.FC<RadioProps> = ({ checked = false, isDisabled = false, label, onChange, style }) => {
	const touchableProps = {
		disabled: isDisabled || !onChange,
		onPress: onChange ? () => onChange(!checked) : undefined,
	};

	const radioColor = isDisabled ? StyleSheet.value('$colorDisabled') : StyleSheet.value('$colorBrandQuaternary');
	const labelColor = isDisabled ? StyleSheet.value('$colorDisabled') : undefined;

	return (
		<Pressable {...touchableProps} activeOpacity={0.6}>
			<View style={[styles.content, style]}>
				<WBIcon name={checked ? 'radio-on' : 'radio-off'} color={radioColor} size={20} style={styles.radio} />
				<WBText style={styles.label} color={labelColor}>
					{label}
				</WBText>
			</View>
		</Pressable>
	);
};
